import { useMemo } from 'react';
import { Group, TextInput, MultiSelect, Box, Select } from '@mantine/core';
import { Search, List, Calendar, Users, User, Layers } from 'react-feather';
import { ButtonToggle, type ButtonToggleOption } from '../../../../components/common';

export type SessionTabValue = 'all' | 'groupCoaching' | 'oneOnOne' | 'workshop';

interface SessionCounts {
  all: number;
  groupCoaching: number;
  oneOnOne: number;
  workshop: number;
}

interface SessionsSubNavProps {
  activeTab: SessionTabValue;
  onTabChange: (tab: SessionTabValue) => void;
  counts: SessionCounts;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  viewMode: 'list' | 'calendar';
  onViewModeChange: (mode: 'list' | 'calendar') => void;
  groupFilter: string[];
  onGroupFilterChange: (groupIds: string[]) => void;
  coachFilter: string[];
  onCoachFilterChange: (coaches: string[]) => void;
  groups?: { id: string; name: string }[];
  coaches?: string[];
  showGroupCoaching?: boolean;
  showOneOnOne?: boolean;
  showWorkshop?: boolean;
  // MED-702: by-session grouping toggle ("View by Date" / "View by Session")
  groupingMode?: 'date' | 'session';
  onGroupingModeChange?: (mode: 'date' | 'session') => void;
  showGroupingToggle?: boolean;
}

/**
 * Sub-navigation bar for Sessions tab
 * Shows tabs for All, Group Coaching, 1:1 Coaching, Workshops
 * Plus search, view toggle, group filter, and coach filter
 */
export function SessionsSubNav({
  activeTab,
  onTabChange,
  counts,
  searchQuery,
  onSearchChange,
  viewMode,
  onViewModeChange,
  groupFilter,
  onGroupFilterChange,
  coachFilter,
  onCoachFilterChange,
  groups = [],
  coaches = [],
  showGroupCoaching = true,
  showOneOnOne = true,
  showWorkshop = true,
  groupingMode = 'date',
  onGroupingModeChange,
  showGroupingToggle = false,
}: SessionsSubNavProps) {
  // Build group options for filter
  const groupOptions = groups.map((g) => ({ value: g.id, label: g.name }));

  // Build coach options for filter
  const coachOptions = coaches.map((name) => ({ value: name, label: name }));

  // Build session type tab options dynamically based on visibility flags
  const sessionTabOptions = useMemo(() => {
    const opts: ButtonToggleOption<SessionTabValue>[] = [
      { value: 'all', label: 'All', badge: counts.all },
    ];
    if (showGroupCoaching) {
      opts.push({ value: 'groupCoaching', label: 'Group Coaching', icon: Users, badge: counts.groupCoaching });
    }
    if (showOneOnOne) {
      opts.push({ value: 'oneOnOne', label: '1:1 Coaching', icon: User, badge: counts.oneOnOne });
    }
    if (showWorkshop) {
      opts.push({ value: 'workshop', label: 'Workshops', icon: Layers, badge: counts.workshop });
    }
    return opts;
  }, [counts, showGroupCoaching, showOneOnOne, showWorkshop]);

  return (
    <Group mb="md" wrap="wrap" gap="sm">
      {/* Left: Session type tabs + Search */}
      <Group gap="sm" wrap="nowrap">
        <ButtonToggle
          options={sessionTabOptions}
          value={activeTab}
          onChange={onTabChange}
        />
        <TextInput
          placeholder="Search..."
          leftSection={<Search size={14} />}
          size="sm"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.currentTarget.value)}
          styles={{ input: { width: 300, height: 38, borderRadius: 6 } }}
        />
      </Group>

      {/* Right: View Toggle, Group Filter, Coach Filter — ml="auto" keeps right-aligned in both single-row and wrapped (narrow viewport) layouts */}
      <Group gap="sm" wrap="nowrap" ml="auto">
        {/* View Mode Toggle */}
        <ButtonToggle
          options={[
            { value: 'list' as const, icon: List, ariaLabel: 'List view' },
            { value: 'calendar' as const, icon: Calendar, ariaLabel: 'Calendar view' },
          ]}
          value={viewMode}
          onChange={onViewModeChange}
        />

        {/* Group Filter */}
        {groups.length > 0 && (
          <Box w={160}>
            <MultiSelect
              data={groupOptions}
              value={groupFilter}
              onChange={onGroupFilterChange}
              size="sm"
              placeholder="All Groups"
              searchable
              clearable
              styles={{ input: { height: 38, minHeight: 38, maxHeight: 38, borderRadius: 6 } }}
            />
          </Box>
        )}

        {/* Coach Filter */}
        {coaches.length > 0 && (
          <Box w={160}>
            <MultiSelect
              data={coachOptions}
              value={coachFilter}
              onChange={onCoachFilterChange}
              size="sm"
              placeholder="All Coaches"
              searchable
              clearable
              styles={{ input: { height: 38, minHeight: 38, maxHeight: 38, borderRadius: 6 } }}
            />
          </Box>
        )}

        {/* MED-702: View by Date / View by Session */}
        {showGroupingToggle && onGroupingModeChange && (
          <Box w={170}>
            <Select
              data={[
                { value: 'date', label: 'View by Date' },
                { value: 'session', label: 'View by Session' },
              ]}
              value={groupingMode}
              onChange={(v) => v && onGroupingModeChange(v as 'date' | 'session')}
              size="sm"
              allowDeselect={false}
              styles={{ input: { height: 38, borderRadius: 6 } }}
            />
          </Box>
        )}
      </Group>
    </Group>
  );
}

export default SessionsSubNav;
